import { useCallback, useEffect, useMemo, useState } from "react";
import { api, errMsg, itDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { CaretLeft, CaretRight, Umbrella } from "@phosphor-icons/react";

const MESI = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
const giorniMese = (y, m) => new Date(y, m + 1, 0).getDate();
const iso = (y, m, d) => `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

const tinta = (n) => (n >= 3 ? "bg-rose-500" : n === 2 ? "bg-amber-400" : "bg-emerald-500");

export default function VacationCalendar() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/ferie/calendario", { params: { year } });
      setData(r.data);
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  }, [year]);
  useEffect(() => { load(); }, [load]);

  const righe = useMemo(() => (data?.rows || []).filter((r) =>
    `${r.name} ${r.code || ""} ${r.department || ""}`.toLowerCase().includes(q.toLowerCase())),
  [data, q]);

  const mesi = useMemo(() => MESI.map((label, m) => ({
    label, m, giorni: Array.from({ length: giorniMese(year, m) }, (_, i) => i + 1),
  })), [year]);

  return (
    <div data-testid="vacation-calendar-page" className="space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-head font-bold tracking-tight">Calendario Ferie</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Anno {year} · {righe.length} dipendenti in ferie · {data?.overlaps?.length || 0} periodi con sovrapposizioni
          </p>
        </div>
        <div className="flex items-center gap-1.5">
          <Button variant="outline" size="icon" className="h-9 w-9 rounded-sm" data-testid="year-prev"
            onClick={() => setYear(year - 1)}><CaretLeft size={16} /></Button>
          <span className="px-2 text-sm font-bold tabular-nums" data-testid="year-label">{year}</span>
          <Button variant="outline" size="icon" className="h-9 w-9 rounded-sm" data-testid="year-next"
            onClick={() => setYear(year + 1)}><CaretRight size={16} /></Button>
        </div>
      </div>

      <div className="rounded-sm border border-border bg-card p-3 flex flex-wrap items-center gap-3">
        <Input data-testid="calendar-search" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Cerca dipendente..." className="h-9 rounded-sm max-w-xs" />
        <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
          {[["bg-emerald-500", "1 persona"], ["bg-amber-400", "2 persone"], ["bg-rose-500", "3 o più"]]
            .map(([c, l]) => (
              <span key={l} className="flex items-center gap-1.5">
                <span className={`inline-block h-3 w-3 rounded-sm ${c}`} />{l}
              </span>
            ))}
        </div>
      </div>

      {loading && <p className="text-sm text-muted-foreground py-8 text-center">Caricamento...</p>}

      {!loading && righe.length === 0 && (
        <div className="rounded-sm border border-border bg-card p-8 text-center text-muted-foreground"
          data-testid="calendar-empty">
          Nessuna ferie registrata nel {year}
        </div>
      )}

      {!loading && righe.length > 0 && (
        <div className="rounded-sm border border-border bg-card overflow-x-auto scroll-thin">
          <div className="min-w-max">
            <div className="flex sticky top-0 bg-secondary z-10 border-b border-border">
              <div className="w-[190px] shrink-0 px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Dipendente
              </div>
              {mesi.map((mm) => (
                <div key={mm.label} className="px-1 border-l border-border">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground text-center py-1">
                    {mm.label}
                  </div>
                  <div className="flex">
                    {mm.giorni.map((d) => (
                      <div key={d} className="w-[9px] text-[7px] text-center text-muted-foreground tabular-nums">
                        {d % 5 === 0 ? d : ""}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {righe.map((r) => {
              const set = new Set(r.days);
              return (
                <div key={r.id} className="flex items-stretch border-b border-border hover:bg-secondary/40"
                  data-testid={`calendar-row-${r.id}`}>
                  <div className="w-[190px] shrink-0 px-3 py-1.5">
                    <div className="text-xs font-semibold truncate">{r.name}</div>
                    <div className="text-[10px] text-muted-foreground tabular-nums">
                      {r.code} · {r.total} giorni
                    </div>
                  </div>
                  {mesi.map((mm) => (
                    <div key={mm.label} className="px-1 border-l border-border flex items-center">
                      {mm.giorni.map((d) => {
                        const g = iso(year, mm.m, d);
                        const n = data.counts[g] || 0;
                        return (
                          <div key={d} title={set.has(g) ? `${r.name} — ${itDate(g)} (${n} in ferie)` : itDate(g)}
                            className={`w-[9px] h-5 border-r border-card ${set.has(g) ? tinta(n) : "bg-secondary/60"}`} />
                        );
                      })}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {!loading && (data?.overlaps?.length || 0) > 0 && (
        <div className="space-y-2">
          <h2 className="text-base md:text-lg font-head font-bold flex items-center gap-2">
            <Umbrella size={18} /> Sovrapposizioni
          </h2>
          <div className="rounded-sm border border-border bg-card overflow-x-auto scroll-thin">
            <table className="w-full text-sm" data-testid="overlaps-table">
              <thead className="bg-secondary">
                <tr>{["Dal", "Al", "Giorni", "In ferie", "Dipendenti"].map((h) => (
                  <th key={h} className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap">{h}</th>
                ))}</tr>
              </thead>
              <tbody>
                {data.overlaps.map((o) => (
                  <tr key={o.date_from} className="border-t border-border"
                    data-testid={`overlap-row-${o.date_from}`}>
                    <td className="px-3 py-1.5 tabular-nums">{itDate(o.date_from)}</td>
                    <td className="px-3 py-1.5 tabular-nums">{itDate(o.date_to)}</td>
                    <td className="px-3 py-1.5 tabular-nums">{o.days}</td>
                    <td className="px-3 py-1.5">
                      <span className={`px-1.5 py-0.5 rounded-sm text-[10px] font-bold text-white ${tinta(o.max)}`}>
                        {o.max}
                      </span>
                    </td>
                    <td className="px-3 py-1.5 text-xs text-muted-foreground">{o.employees.join(" · ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
